import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'components/elements/texts/Text'
import Modal from './Modal'
import { ContractInfoInModal } from './ContractInfoInModal'
import {
  CargoQuantityTypes,
  CargoTypes,
  DisplacementTypes,
  initialQnEditState,
  QnEditStateProps,
  QnState,
  RelationshipTypes,
  VehicleTypes,
} from 'states/QnState'
import { PAGE_PATH } from '../../../constants/pagePath'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { getGtag } from 'utils/gtag'
import { success } from 'utils/status'
import { CONTRACT_INFO_FORM } from 'constants/qnState'
import {
  useReceptionNo,
  useEditContractInfo,
  useQNStateKey,
  useCloneState,
} from 'utils/commonHooks'
import {
  useIntermediateSendAction,
  useSendApi,
  useSendImageAction,
  useSendRequestAction,
} from 'utils/informRequester'

/**
 * オペレーターに入力内容を連携しない旨の注意書きを表示する画面の一覧
 * 表示対象は、「予約画面」「車の状況画面」
 */
const fillingPagePathList: Array<string> = [
  PAGE_PATH.RESERVATION,
  PAGE_PATH.TROUBLE_PLACE,
]

/**
 * 架電モーダル 呼び出し元を含む
 */
const CallModal: React.FC<CallModalProps> = (props) => {
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [firstNumber, setFirstNumber] = useState('')
  const [secondNumber, setSecondNumber] = useState('')
  const [lastNumber, setLastNumber] = useState('')
  const [editState, setEditingItem] = useEditContractInfo()
  const [hasFullNameError, setHasFullNameError] = useState(true)
  const [hasPhoneNumberError, setHasPhoneError] = useState(true)
  const [sendAction, setSendAction] = useState(false)
  const [on, setOn] = useState(!props.children)
  const receptionNo = useReceptionNo()
  const history = useHistory()
  const qnStates = useCloneState(useQNStateKey, QnState)
  const { state, setState } = qnStates
  // --- 要請者写真登録
  const sendImageAction = useSendImageAction(state)
  // --- 要請者入力情報連携
  const sendRequestAction = useSendRequestAction(state, '1')
  const [errorCount, setErrorCount] = useState(0)
  const sendApi = useSendApi(sendImageAction, sendRequestAction)
  const forwardAction = useIntermediateSendAction(
    sendApi,
    setErrorCount,
    errorCount,
    history,
    state,
    setState
  )
  const pathName = history.location.pathname
  const inputPhoneNumber = firstNumber + secondNumber + lastNumber

  /**
   * 新ヘルプボタンを表示するパス
   * 下記に該当しないものは通常のヘルプボタンが適応される
   */
  const contractInfoInModalPagePathList = [
    PAGE_PATH.VEHICLE_STATE,
    PAGE_PATH.ENGINE_TROUBLE,
    PAGE_PATH.FLAT_TIRE,
    PAGE_PATH.ACCIDENT,
    PAGE_PATH.FALLING_WHEEL,
    PAGE_PATH.EMPTY_FUEL,
    PAGE_PATH.LOSS_KEY,
    PAGE_PATH.OTHERS,
    PAGE_PATH.TRANSPORT_DESTINATION,
    PAGE_PATH.TROUBLE_PLACE_SURROUNDING,
    PAGE_PATH.SERVICE_INFORMATION,
    PAGE_PATH.CONTRACT_INFORMATION,
    PAGE_PATH.CONFIRMATION,
  ].includes(pathName)

  /** 架電ポップアップに表示するメッセージ */
  const modalMessage: string = React.useMemo(() => {
    const message = 'ロードアシスタンスのオペレーターへ電話をかけますか？'

    /** 架電ポップアップのメッセージに注意書きを表示するかのフラグ */
    const isDisplayAnnotation: boolean = fillingPagePathList.some(
      (pagePath) => pagePath === history.location.pathname
    )

    if (isDisplayAnnotation) {
      return message.concat(
        '\n※ここまでの入力内容はオペレーターに連携されません。'
      )
    }
    return message
  }, [history.location.pathname])

  // call クリック時に EditingItem & QnStateへ反映する処理
  const selectionEditingItemUpdate = useCallback((): void => {
    const setEditingItemContractInfo = (): void => {
      const editingItemContractInfo = JSON.parse(
        localStorage.getItem('EditingItem') as string
      )
      localStorage.setItem('QnState', JSON.stringify(editingItemContractInfo))
      return editingItemContractInfo.contractInfo
    }
    // 途中架電されたページパスを対象に 初期化する内容を判別
    const changeInitialState = {
      [PAGE_PATH.TROUBLE_PLACE_SURROUNDING]: {
        locationStateInfo: initialQnEditState.locationStateInfo,
      },
      [PAGE_PATH.VEHICLE_STATE]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.ENGINE_TROUBLE]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.FLAT_TIRE]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.ACCIDENT]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.FALLING_WHEEL]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.EMPTY_FUEL]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.LOSS_KEY]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.OTHERS]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.TRANSPORT_DESTINATION]: {
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: '',
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      },
      [PAGE_PATH.CONTRACT_INFORMATION]: {},
      [PAGE_PATH.CONFIRMATION]: {},
      [PAGE_PATH.SERVICE_INFORMATION]: {},
    }
    const getPageSpecificState = (
      path: keyof typeof PAGE_PATH
    ): Partial<QnEditStateProps> => {
      return changeInitialState[path] || {}
    }
    const sharedState = getPageSpecificState(pathName)
    const baseEditState = { ...editState, ...sharedState }
    const baseState = { ...state, ...sharedState }
    const isPagePathContractInfo = pathName === PAGE_PATH.CONTRACT_INFORMATION
    const inputNameKana =
      lastName !== '' && firstName !== '' ? `${lastName} ${firstName}` : ''
    const relationshipConfirmation =
      editState.contractInfo.relationship === RelationshipTypes.self &&
      editState.contractInfo.contractorName !== '' &&
      inputNameKana !== '' &&
      editState.contractInfo.contractorName !== inputNameKana

    // 名前 電話番号 共にエラーがない場合
    if (!hasFullNameError && !hasPhoneNumberError) {
      setEditingItem({
        ...baseEditState,
        contractInfo: {
          ...editState.contractInfo,
          nameKana: `${lastName}\u3000${firstName}`,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: inputPhoneNumber,
          firstNumber: firstNumber,
          secondNumber: secondNumber,
          lastNumber: lastNumber,
          // 「 QN-06 契約者情報画面 」で途中架電された場合は下記を初期化／そうでない場合はEditingItemを保持
          contractorName: isPagePathContractInfo
            ? ''
            : editState.contractInfo.contractorName,
          plateNumber: isPagePathContractInfo
            ? ''
            : editState.contractInfo.plateNumber,
          bikeDisplacement: isPagePathContractInfo
            ? ''
            : editState.contractInfo.bikeDisplacement,
          cargo: isPagePathContractInfo ? '' : editState.contractInfo.cargo,
          cargoQuantity: isPagePathContractInfo
            ? ''
            : editState.contractInfo.cargoQuantity,
          relationship: isPagePathContractInfo
            ? ''
            : relationshipConfirmation
            ? RelationshipTypes.other
            : editState.contractInfo.relationship,
          vehicleType: isPagePathContractInfo
            ? ''
            : editState.contractInfo.vehicleType,
        },
      })
      setState({
        ...baseState,
        contractInfo: setEditingItemContractInfo(),
      })
      return
    }
    // 名前のみにエラーがない場合（電話番号が空またはエラーがある状態）
    if (!hasFullNameError) {
      setEditingItem({
        ...baseEditState,
        contractInfo: {
          ...editState.contractInfo,
          nameKana: `${lastName}\u3000${firstName}`,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: '',
          firstNumber: '',
          secondNumber: '',
          lastNumber: '',
          // 「 QN-06 契約者情報画面 」で途中架電された場合は下記を初期化／そうでない場合はEditingItemを保持
          contractorName: isPagePathContractInfo
            ? ''
            : editState.contractInfo.contractorName,
          plateNumber: isPagePathContractInfo
            ? ''
            : editState.contractInfo.plateNumber,
          bikeDisplacement: isPagePathContractInfo
            ? ''
            : editState.contractInfo.bikeDisplacement,
          cargo: isPagePathContractInfo ? '' : editState.contractInfo.cargo,
          cargoQuantity: isPagePathContractInfo
            ? ''
            : editState.contractInfo.cargoQuantity,
          relationship: isPagePathContractInfo
            ? ''
            : relationshipConfirmation
            ? RelationshipTypes.other
            : editState.contractInfo.relationship,
          vehicleType: isPagePathContractInfo
            ? ''
            : editState.contractInfo.vehicleType,
        },
      })
      setState({
        ...baseState,
        contractInfo: setEditingItemContractInfo(),
      })
      return
    }
    // 電話番号のみにエラーがない場合（名前が空またはエラーがある状態）
    if (!hasPhoneNumberError) {
      setEditingItem({
        ...baseEditState,
        contractInfo: {
          ...editState.contractInfo,
          phoneNumber: inputPhoneNumber,
          firstNumber: firstNumber,
          secondNumber: secondNumber,
          lastNumber: lastNumber,
          nameKana: '',
          firstName: '',
          lastName: '',
          // 「 QN-06 契約者情報画面 」で途中架電された場合は下記を初期化／そうでない場合はEditingItemを保持
          contractorName: isPagePathContractInfo
            ? ''
            : editState.contractInfo.contractorName,
          plateNumber: isPagePathContractInfo
            ? ''
            : editState.contractInfo.plateNumber,
          bikeDisplacement: isPagePathContractInfo
            ? ''
            : editState.contractInfo.bikeDisplacement,
          cargo: isPagePathContractInfo ? '' : editState.contractInfo.cargo,
          cargoQuantity: isPagePathContractInfo
            ? ''
            : editState.contractInfo.cargoQuantity,
          relationship: isPagePathContractInfo
            ? ''
            : editState.contractInfo.relationship,
          vehicleType: isPagePathContractInfo
            ? ''
            : editState.contractInfo.vehicleType,
        },
      })
      setState({
        ...baseState,
        contractInfo: setEditingItemContractInfo(),
      })
      return
    }
  }, [
    editState,
    firstName,
    lastName,
    firstNumber,
    secondNumber,
    lastNumber,
    hasFullNameError,
    hasPhoneNumberError,
  ])

  // call 押下時に架電タイプを判断するため、ContractInfoInModal からエラー状況を受け取る
  const handleFullNameError = (hasFullNameError: boolean): void => {
    setHasFullNameError(hasFullNameError)
  }
  const handleFullNumberError = (hasPhoneNumberError: boolean): void => {
    setHasPhoneError(hasPhoneNumberError)
  }

  // modal開閉
  const modalOpen = useCallback(() => {
    setOn(true)
    setHasFullNameError(true)
    setHasPhoneError(true)
  }, [])
  const close = useCallback(() => {
    setOn(false)
    if (props.onClose) {
      props.onClose()
    }
  }, [])

  // 「はい」押下時
  const call = useCallback(() => {
    if (hasFullNameError === false || hasPhoneNumberError === false) {
      // 送信処理中の際はEditingItemへ値を再度セットし画面上でラジオボタンが消失しないようにする
      const initialEditState = (): void => {
        setEditingItem({
          ...editState,
          contractInfo: {
            ...editState.contractInfo,
            relationship: RelationshipTypes['']
              ? RelationshipTypes.self
              : editState.contractInfo.relationship,
            bikeDisplacement: DisplacementTypes['']
              ? DisplacementTypes.under50
              : editState.contractInfo.bikeDisplacement,
            vehicleType: VehicleTypes['']
              ? VehicleTypes.autoMobile
              : editState.contractInfo.vehicleType,
            cargo: CargoTypes['']
              ? CargoTypes.lightTruck
              : editState.contractInfo.cargo,
            cargoQuantity: CargoQuantityTypes['']
              ? CargoQuantityTypes.full
              : editState.contractInfo.cargoQuantity,
          },
        })
      }
      selectionEditingItemUpdate()
      setSendAction(true)
      initialEditState()
      const gtag = getGtag()
      gtag('event', '受付番号_架電', {
        ['caller_reception_no']: receptionNo,
      })
    } else {
      // 名前と電話番号が空欄または、エラーを保持している状態で架電された場合
      const gtag = getGtag()
      gtag('event', '受付番号_架電', { ['caller_reception_no']: receptionNo })
      props.isTransitionReAccess && history.push(PAGE_PATH.RE_ACCESS)
    }
    setOn(false)
    return success()
  }, [
    props.isTransitionReAccess,
    editState,
    firstName,
    lastName,
    firstNumber,
    secondNumber,
    lastNumber,
    hasFullNameError,
    hasPhoneNumberError,
  ])

  // EditingItemとQnStateの情報が精査され上書きされたら、通信を開始する
  useEffect(() => {
    if (sendAction === true) {
      forwardAction()
    }
  }, [sendAction])

  return (
    <>
      {props.children && <div onClick={modalOpen}>{props.children}</div>}
      <Modal
        on={props.children ? on : props.isOpened ?? false}
        type="center"
        greyBack
        scrollLock
      >
        <CallModalArea>
          <Text>{modalMessage}</Text>
          {contractInfoInModalPagePathList && (
            <ContractInfoInModal
              placeholderFirstNumber={CONTRACT_INFO_FORM.phoneNumber.label[0]}
              placeholderSecondNumber={CONTRACT_INFO_FORM.phoneNumber.label[1]}
              placeholderLastNumber={CONTRACT_INFO_FORM.phoneNumber.label[2]}
              placeholderLastName={CLIENT_SETTINGS.label.contractInfo.lastName}
              placeholderFirstName={
                CLIENT_SETTINGS.label.contractInfo.firstName
              }
              inputTextFirstNumber={firstNumber}
              inputTextSecondNumber={secondNumber}
              inputTextLastNumber={lastNumber}
              inputTextLastName={lastName}
              inputTextFirstName={firstName}
              onChangeFirstNumber={setFirstNumber}
              onChangeSecondNumber={setSecondNumber}
              onChangeLastNumber={setLastNumber}
              setLastName={setLastName}
              setFirstName={setFirstName}
              onErrorChangePhoneNumber={handleFullNumberError}
              onErrorChangeFullName={handleFullNameError}
            />
          )}
          <CallModalActionArea>
            <CallAction onClick={close}>いいえ</CallAction>
            <CallAction onClick={call} bold href={`tel:${CLIENT_SETTINGS.tel}`}>
              はい
            </CallAction>
          </CallModalActionArea>
        </CallModalArea>
      </Modal>
    </>
  )
}

type CallModalProps = {
  isTransitionReAccess: boolean
  children?: React.ReactNode
  isOpened?: boolean
  onClose?: () => void
}

const CallModalArea = styled.div`
  width: calc(100vw - 72px);
  padding: 16px;
  color: #111111;
  text-align: left;
  background: #ffffff;
  border-radius: 6px;
  pointer-events: auto;
`

const CallModalActionArea = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`

const CallAction = styled.a<{ bold?: boolean }>`
  margin-left: 24px;
  color: #1e90ff;
  ${(props): string => (props.bold ? 'font-weight: bold' : '')};
`

export default React.memo<CallModalProps>(CallModal)
